import intersection from 'lodash/intersection'
import mime from 'mime/lite'
import uploadcare from 'uploadcare-widget'
import type { UploadcareFileInfo, UploadcareResult } from 'uploadcare-widget'
import uploadcareTabEffects from 'uploadcare-widget-tab-effects'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'
import { UploadSource } from '@buffer-mono/uploader'
import type { BufferUpload, Uploader } from '@buffer-mono/uploader'

import {
  gifsAllowed,
  LegacyUploadcareServiceCropMap,
  UploadcareSources,
} from '~publish/components/UploadcareImageDialog/constants'
import { env } from '~publish/env'
import type { Service } from '~publish/legacy/constants/services/graphqlTypes'
import ComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'
import { UploadTypes } from '~publish/legacy/constants'
import { selectCurrentOrganizationId } from '~publish/legacy/organizations/selectors'
import { store } from '~publish/legacy/store'
import { getDebugLogger } from '~publish/legacy/utils/getDebugLogger'

import ComposerActionCreators from '../composer/composer/action-creators/ComposerActionCreators'
import { PostTypeStory } from '../post/constants'

const debug = getDebugLogger('uploadcare')

type UploadcareUploader = Pick<Uploader, 'addFilesFromUrls'> &
  Partial<Pick<Uploader, 'on' | 'off'>>

// This is required because 'ideas' is not allowed in the tracking plan
const getChannel = (serviceId: string): string =>
  serviceId === 'ideas' ? 'multiple' : serviceId

const onUploadDone = <T extends 'edit' | 'bar'>(
  result: UploadcareResult,
  uploader: UploadcareUploader,
  onError: (error: { message: string }) => void,
  onUploadcareUploadFinished: () => void,
  isEdit: boolean,
  draftData: T extends 'edit'
    ? { url?: string; deleteCallback?: () => void; altText?: string }
    : undefined,
  serviceId: string,
  source: T,
  integration: string,
  context: string,
): void => {
  try {
    if (isEdit) {
      draftData?.deleteCallback?.()
    }
  } catch (e) {
    if (e instanceof Error) {
      debug(e.message, e)
    }
  }

  result
    .promise()
    .done(async (response) => {
      try {
        // .mov is preferred for quicktime video files and many services reject files with .qt extension
        // so we always replace .qt with .mov
        // solves CONT-512: https://linear.app/buffer/issue/CONT-512/dropbox-integration-fails-when-uploading-mov-files-with-cannot-read
        // @see https://github.com/broofa/mime/issues/277
        const extensionFromMimeType = mime.getExtension(response.mimeType ?? '')
        const extension =
          extensionFromMimeType === 'qt' ? 'mov' : extensionFromMimeType

        // Handle empty or whitespace-only filenames from uploadcare
        // This can happen with OneDrive uploads in newer uploadcare versions
        const baseName =
          response.name && response.name.trim() && response.name !== '.'
            ? response.name
            : `file-${Date.now()}${extension ? `.${extension}` : ''}`

        const filename =
          !extension || baseName.endsWith(`.${extension}`)
            ? baseName
            : `${baseName}.${extension}`

        // If present - preserve the alt text
        const altText = draftData?.altText
        if (isEdit && altText) {
          function handleUploadFinished(file: BufferUpload): void {
            const image = ComposerStore.getDraft(serviceId)?.getImageByUrl(
              file.url,
            )

            if (image && image.source?.name === 'uploadcare' && altText) {
              ComposerActionCreators.updateImageAltText(image, altText)
            }

            uploader.off?.('upload-finished', handleUploadFinished)
          }

          uploader.on?.('upload-finished', handleUploadFinished)
        }

        await uploader.addFilesFromUrls(
          { remoteUrl: response.cdnUrl, name: filename },
          {
            source: UploadSource.integration(integration),
          },
        )

        const media = response.mimeType?.includes('image') ? 'photo' : 'video'

        BufferTracker.uploadcareUploadFinished({
          organizationId: selectCurrentOrganizationId(store.getState()),
          clientName: 'publishWeb',
          product: 'publish',
          channel: getChannel(serviceId),
          source,
          integration,
          media,
          context,
        })
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e)
        debug(message, e)
        onError({ message })
      } finally {
        // The composer disables posting while this flag is set, so it has to be
        // cleared even when the upload throws. Otherwise the draft stays locked
        // until the page is reloaded.
        onUploadcareUploadFinished()
      }
    })
    .fail((message: string) => {
      /**
       * If error message = 'upload', it means the file size is too big (over 100MB,
       * which is allowed by our plan). It's returned by Uploadcare servers, so we change
       * it to something more meaningful
       */
      if (message === 'upload') {
        message = 'File size should be less than 100MB'
      }

      try {
        onUploadcareUploadFinished()
        debug(message)
        onError({ message })
      } catch (e) {
        if (e instanceof Error) debug(e.message, e)
      }
    })
}

const imagesAndVideosOnly = (fileInfo: UploadcareFileInfo): void => {
  if (
    fileInfo.mimeType !== null &&
    fileInfo.isImage === false &&
    !fileInfo.mimeType.includes('video')
  ) {
    throw new Error('Files other than videos and images are not supported.')
  }
}

const fileSizeStories = (fileInfo: UploadcareFileInfo): void => {
  if (
    fileInfo.size !== null &&
    fileInfo.size > 8 * 1024 * 1024 &&
    fileInfo.isImage === true
  ) {
    throw new Error('Image has to be less than 8MB.')
  }

  if (
    fileInfo.size !== null &&
    fileInfo.size > 50 * 1024 * 1024 &&
    fileInfo.mimeType !== null &&
    fileInfo.mimeType.includes('video')
  ) {
    throw new Error('Video has to be less than 50MB.')
  }

  if (
    fileInfo.size !== null &&
    fileInfo.size > 3 * 1024 * 1024 &&
    fileInfo.mimeType !== null &&
    fileInfo.mimeType.includes('image/gif')
  ) {
    throw new Error('GIF has to be less than 3MB.')
  }
}

const fileSize = (fileInfo: UploadcareFileInfo): void => {
  if (
    fileInfo.size !== null &&
    fileInfo.size > 10 * 1024 * 1024 &&
    fileInfo.isImage === true
  ) {
    throw new Error('Image has to be less than 10MB.')
  }

  if (
    fileInfo.size !== null &&
    fileInfo.size > 1024 * 1024 * 1024 &&
    fileInfo.mimeType !== null &&
    fileInfo.mimeType.includes('video')
  ) {
    throw new Error('Video has to be less than 1GB.')
  }

  if (
    fileInfo.size !== null &&
    fileInfo.size > 3 * 1024 * 1024 &&
    fileInfo.mimeType !== null &&
    fileInfo.mimeType.includes('image/gif')
  ) {
    throw new Error('GIF has to be less than 3MB.')
  }
}

const gifsNotAllowed = (fileInfo: UploadcareFileInfo): void => {
  if (fileInfo.mimeType !== null && fileInfo.mimeType.includes('image/gif')) {
    throw new Error('Gifs are not supported for this network.')
  }
}

const imagesOnlyAllowed = (fileInfo: UploadcareFileInfo): void => {
  if (
    fileInfo.mimeType !== null &&
    (fileInfo.mimeType.includes('video') || fileInfo.mimeType.includes('gif'))
  ) {
    throw new Error('For this network, we support only image upload.')
  }
}

const imagesAndGifsOnlyAllowed = (fileInfo: UploadcareFileInfo): void => {
  if (fileInfo.mimeType !== null && fileInfo.mimeType.includes('video')) {
    throw new Error('For this network, we support only image or gif upload.')
  }
}

type UploadCareOptions = {
  publicKey: string
  imagesOnly: boolean
  tabs: string
  previewStep: boolean
  effects: string
  crop?: string
}

export const getUploadCareEditOptions = ({
  serviceId,
  updateType,
}: {
  serviceId: Service | 'omni' | 'ideas'
  updateType?: string
}): {
  uploadcareStartOptions: {
    effects: string[]
  }
  uploadcareOptions: UploadCareOptions
} => {
  const uploadcareStartOptions = {
    effects: [
      'blur',
      'sharp',
      'grayscale',
      'crop',
      'rotate',
      'enhance',
      'mirror',
      'flip',
      'invert',
    ],
  }
  const uploadcareOptions: UploadCareOptions = {
    publicKey: env.VITE_UPLOADCARE_KEY,
    imagesOnly: true,
    tabs: 'preview',
    previewStep: true,
    effects: 'all',
  }

  if (serviceId === 'omni') {
    const enabledMaps = ComposerStore.getEnabledDrafts().map(
      (draft) => LegacyUploadcareServiceCropMap[draft.id],
    )

    const intersectedCrops = [
      ...intersection(...enabledMaps),
      ...LegacyUploadcareServiceCropMap.omni,
    ]
    uploadcareOptions.crop = [...new Set(intersectedCrops)].join(',')
  } else {
    uploadcareOptions.crop = LegacyUploadcareServiceCropMap[serviceId].join(',')

    if (updateType === PostTypeStory) {
      uploadcareOptions.crop +=
        ',' + LegacyUploadcareServiceCropMap.instagramStory.join(',')
    }
  }

  return { uploadcareStartOptions, uploadcareOptions }
}

export const uploadcareValidators = {
  fileSize,
  gifsNotAllowed,
  imagesOnlyAllowed,
  imagesAndVideosOnly,
  imagesAndGifsOnlyAllowed,
}

export const getUploadCareIntegrationsOptions = ({
  serviceId,
  integration,
  updateType,
}: {
  serviceId: Service | 'omni' | 'ideas'
  integration: string
  updateType?: string
}): {
  uploadcareOptions: Omit<
    UploadCareOptions,
    'previewStep' | 'effects' | 'imagesOnly'
  >
  uploadcareStartOptions: undefined
} => {
  const uploadcareOptions = {
    publicKey: env.VITE_UPLOADCARE_KEY,
    inputAcceptTypes: 'image/*,video/*',
    tabs: integration,
    validators: [imagesAndVideosOnly],
  }

  if (updateType === PostTypeStory) {
    uploadcareOptions.validators.push(fileSizeStories)
  } else {
    uploadcareOptions.validators.push(fileSize)
  }

  if (!gifsAllowed.includes(serviceId)) {
    uploadcareOptions.validators.push(gifsNotAllowed)
  }

  if (serviceId === 'googlebusiness') {
    uploadcareOptions.validators.push(imagesOnlyAllowed)
  }

  if (serviceId === 'startPage') {
    uploadcareOptions.validators.push(imagesAndGifsOnlyAllowed)
  }

  return {
    uploadcareOptions,
    uploadcareStartOptions: undefined,
  }
}

const openUploadcareModal = <T extends 'edit' | 'bar'>(
  serviceId: Service | 'omni' | 'ideas',
  uploader: UploadcareUploader,
  onError: (error: { message: string }) => void,
  integration: string,
  onUploadcareUploadStarted: () => void,
  onUploadcareUploadFinished: () => void,
  source: T,
  draftData: T extends 'edit'
    ? { url?: string; deleteCallback?: () => void; altText?: string }
    : undefined,
  updateType = String(UploadTypes.MEDIA),
  context = 'publishComposer',
): void => {
  const isEdit = source === UploadcareSources.EDIT
  if (isEdit) {
    BufferTracker.uploadcareEditClicked({
      organizationId: selectCurrentOrganizationId(store.getState()),
      clientName: 'publishWeb',
      product: 'publish',
      channel: getChannel(serviceId),
      source,
      integration,
      context,
    })
  } else {
    BufferTracker.uploadcareIntegrationButtonClicked({
      organizationId: selectCurrentOrganizationId(store.getState()),
      clientName: 'publishWeb',
      product: 'publish',
      channel: getChannel(serviceId),
      source,
      integration,
      context,
    })
  }

  // 1. Init options
  const { uploadcareStartOptions = {}, uploadcareOptions } = isEdit
    ? getUploadCareEditOptions({ serviceId, updateType })
    : getUploadCareIntegrationsOptions({ serviceId, integration, updateType })

  // 2. Init Uploadcare
  uploadcare.start(uploadcareStartOptions)

  // 3. Inject styles for iframe tabs (Google Drive, Dropbox, etc.)
  // Read CSS variable values from parent document since they don't cascade into iframes
  // This is required for getting the colors right based on theme
  const styles = getComputedStyle(document.documentElement)
  const cssVar = (name: string): string => styles.getPropertyValue(name).trim()

  uploadcare.tabsCss.addStyle(`
      body, .main-content, .main-content-header-reserved, .main-content-header, .main-content-footer, .side-bar, input {
        background-color: ${cssVar('--color-bg-surface-floating')};
        color: ${cssVar('--color-text-neutral')};
      }

      .loading, .loading * {
        background-color: ${cssVar('--color-bg-surface-floating')} !important;
        color: ${cssVar('--color-text-neutral')};
      }

      input[type='submit'] {
        background: ${cssVar('--color-bg-surface-floating')};
        color: ${cssVar('--color-text-neutral')};
      }

      input[type='submit']:hover {
        background: ${cssVar('--color-bg-surface-neutral')};
      }

      .list-items a {
        color: ${cssVar('--color-text-neutral')};
      }

      .list-items a:hover {
        background-color: ${cssVar('--color-bg-surface-neutral')};
      }

      .side-bar-menu .menu-item a {
        color: ${cssVar('--color-text-neutral')};
      }
    `)

  // 4: If edit enabled, register "Edit" tab
  if (isEdit) {
    uploadcare.registerTab('preview', uploadcareTabEffects)
  }

  // 5. Open dialog
  const dialog = uploadcare.openDialog(null, uploadcareOptions)
  BufferTracker.uploadcareModalOpened({
    organizationId: selectCurrentOrganizationId(store.getState()),
    clientName: 'publishWeb',
    product: 'publish',
    channel: getChannel(serviceId),
    source,
    integration,
    context,
  })

  // 6: If edit enabled, fetch the image to Uploadcare
  if (isEdit && draftData?.url) {
    dialog.addFiles('url', [
      [
        draftData.url,
        {
          source: 'url-tab',
        },
      ],
    ])
  }

  dialog.done((result: UploadcareResult) => {
    onUploadcareUploadStarted()
    BufferTracker.uploadcareUploadStarted({
      organizationId: selectCurrentOrganizationId(store.getState()),
      clientName: 'publishWeb',
      product: 'publish',
      channel: getChannel(serviceId),
      source,
      integration,
      context,
    })
    onUploadDone(
      result,
      uploader,
      onError,
      onUploadcareUploadFinished,
      isEdit,
      draftData,
      serviceId,
      source,
      integration,
      context,
    )
  })

  dialog.fail((result: unknown) => {
    debug('Failed uploadcare dialog', result)
  })
}

export { openUploadcareModal }
